package fr.frux.feature.treeadd.wizard

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.outlined.ShoppingBasket
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxNoteField
import fr.frux.core.designsystem.components.FruxPhotoSourceSheet
import fr.frux.core.designsystem.components.StatusTile
import fr.frux.core.designsystem.components.TreeGlyphDisc
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.media.ImageSource
import fr.frux.core.media.rememberImagePicker
import fr.frux.feature.treeadd.resources.Res
import fr.frux.feature.treeadd.resources.treeadd_certainty_certain
import fr.frux.feature.treeadd.resources.treeadd_certainty_probable
import fr.frux.feature.treeadd.resources.treeadd_certainty_to_confirm
import fr.frux.feature.treeadd.resources.wizard_details_hint
import fr.frux.feature.treeadd.resources.wizard_details_status_hint
import fr.frux.feature.treeadd.resources.wizard_details_status_title
import fr.frux.feature.treeadd.resources.wizard_details_title
import fr.frux.feature.treeadd.resources.wizard_note_label
import fr.frux.feature.treeadd.resources.wizard_note_placeholder
import fr.frux.feature.treeadd.resources.wizard_recap
import fr.frux.feature.treeadd.resources.wizard_recap_position
import fr.frux.feature.treeadd.resources.wizard_status_flowering
import fr.frux.feature.treeadd.resources.wizard_status_picked_clean
import fr.frux.feature.treeadd.resources.wizard_status_ripe_ready
import fr.frux.feature.treeadd.resources.wizard_submit
import fr.frux.feature.treeadd.resources.wizard_submit_hint
import fr.frux.feature.treeadd.resources.wizard_submit_uploading_hint
import fr.frux.shared.content.DescriptionLength
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.TreeStatus
import org.jetbrains.compose.resources.stringResource

private val STEP_PADDING = 20.dp
private val STEP_SPACING = 18.dp

/** Tag et description stables du bouton d'envoi (E2E). */
internal const val SUBMIT_TAG = "details_submit"

/** Préfixe des tags des tuiles d'état : `details_status_FLOWERING`, etc. */
internal const val STATUS_TAG_PREFIX = "details_status_"

/**
 * Étape 3 : rappel, état du jour, photos et note, tous facultatifs. Contenu défilant, pied
 * « Ajouter l'arbre » ancré hors du défilement. [onSubmit] sert aussi à quitter pendant l'envoi
 * des photos (`submit()` s'en charge selon l'état).
 */
@Composable
internal fun DetailsStep(
    state: TreeWizardState,
    controller: DetailsStepController,
    onSubmit: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val uploading = state.submission as? WizardSubmission.Uploading
    val idle = state.submission == WizardSubmission.Idle
    val picker =
        rememberImagePicker(
            maxItems = MAX_WIZARD_PHOTOS - state.details.photos.size,
            onResult = controller::addPhoto,
        )
    var showSources by remember { mutableStateOf(false) }

    Column(modifier = modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Column(
            modifier = Modifier.weight(1f).verticalScroll(rememberScrollState()).padding(STEP_PADDING),
            verticalArrangement = Arrangement.spacedBy(STEP_SPACING),
        ) {
            Text(stringResource(Res.string.wizard_details_title), style = MaterialTheme.typography.headlineMedium)
            Text(
                text = stringResource(Res.string.wizard_details_hint),
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            state.species.selected?.let { Recap(it, state.species.certainty) }
            StatusSection(
                selected = state.details.status,
                readyGlyph = state.species.selected?.glyph() ?: FruxSpeciesGlyphs.Fleur,
                enabled = idle,
                onToggle = controller::toggleStatus,
            )
            PhotoStrip(
                photos = state.details.photos,
                uploads = uploading?.uploads,
                canAdd = state.details.canAddPhoto && idle,
                sourcesAvailable = picker.availableSources.isNotEmpty(),
                onAdd = {
                    if (picker.availableSources.size > 1) {
                        showSources = true
                    } else {
                        picker.availableSources.firstOrNull()?.let(picker::launch)
                    }
                },
                onRemove = controller::removePhoto,
                removable = idle,
            )
            FruxNoteField(
                value = state.details.note,
                onValueChange = controller::onNoteChange,
                enabled = idle,
                label = stringResource(Res.string.wizard_note_label),
                placeholder = stringResource(Res.string.wizard_note_placeholder),
                maxLength = DescriptionLength.MAX,
                showCounter = false,
            )
        }
        DetailsFooter(
            creating = state.submission == WizardSubmission.Creating,
            uploading = uploading != null,
            onSubmit = onSubmit,
        )
    }
    if (showSources) {
        FruxPhotoSourceSheet(
            onCamera = {
                showSources = false
                picker.launch(ImageSource.CAMERA)
            },
            onGallery = {
                showSources = false
                picker.launch(ImageSource.GALLERY)
            },
            onDismiss = { showSources = false },
        )
    }
}

@Composable
private fun Recap(
    species: Species,
    certainty: Certainty,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.surfaceVariant,
    ) {
        Row(
            modifier = Modifier.padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            TreeGlyphDisc(glyph = species.glyph(), tint = speciesTint(species.scientificName), size = 48.dp)
            Column {
                Text(
                    text = stringResource(Res.string.wizard_recap, species.commonNameFr, certainty.label()),
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.onSurface,
                )
                Text(
                    text = stringResource(Res.string.wizard_recap_position),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

@Composable
private fun Certainty.label(): String =
    when (this) {
        Certainty.CERTAIN -> stringResource(Res.string.treeadd_certainty_certain)
        Certainty.PROBABLE -> stringResource(Res.string.treeadd_certainty_probable)
        Certainty.TO_CONFIRM -> stringResource(Res.string.treeadd_certainty_to_confirm)
    }

@Composable
private fun StatusSection(
    selected: TreeStatus?,
    readyGlyph: ImageVector,
    enabled: Boolean,
    onToggle: (TreeStatus) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Text(stringResource(Res.string.wizard_details_status_title), style = MaterialTheme.typography.titleMedium)
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            WIZARD_STATUSES.forEach { status ->
                val look = status.look(readyGlyph)
                StatusTile(
                    label = look.label,
                    icon = look.icon,
                    iconContainer = look.container,
                    iconTint = look.tint,
                    selected = selected == status,
                    onToggle = { onToggle(status) },
                    enabled = enabled,
                    modifier = Modifier.weight(1f).testTag(STATUS_TAG_PREFIX + status.name),
                )
            }
        }
        Text(
            text = stringResource(Res.string.wizard_details_status_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

private class StatusLook(
    val label: String,
    val icon: ImageVector,
    val container: Color,
    val tint: Color,
)

@Composable
private fun TreeStatus.look(readyGlyph: ImageVector): StatusLook {
    val scheme = MaterialTheme.colorScheme
    val extra = MaterialTheme.fruxColors
    return when (this) {
        TreeStatus.FLOWERING -> {
            StatusLook(
                stringResource(Res.string.wizard_status_flowering),
                FruxSpeciesGlyphs.Fleur,
                scheme.tertiaryContainer,
                scheme.onTertiaryContainer,
            )
        }

        TreeStatus.RIPE_READY -> {
            StatusLook(
                stringResource(Res.string.wizard_status_ripe_ready),
                readyGlyph,
                extra.accentLeafContainer,
                extra.statusAvailable,
            )
        }

        else -> {
            StatusLook(
                stringResource(Res.string.wizard_status_picked_clean),
                Icons.Outlined.ShoppingBasket,
                scheme.surfaceVariant,
                scheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun DetailsFooter(
    creating: Boolean,
    uploading: Boolean,
    onSubmit: () -> Unit,
) {
    val label = stringResource(Res.string.wizard_submit)
    Column(modifier = Modifier.fillMaxWidth()) {
        HorizontalDivider(thickness = 1.dp, color = MaterialTheme.colorScheme.outlineVariant)
        Column(
            modifier =
                Modifier.navigationBarsPadding().padding(
                    start = 20.dp,
                    top = 12.dp,
                    end = 20.dp,
                    bottom = 28.dp,
                ),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            FruxButton(
                text = label,
                onClick = onSubmit,
                loading = creating,
                icon = Icons.Filled.Check,
                modifier = Modifier.fillMaxWidth().testTag(SUBMIT_TAG).semantics { contentDescription = label },
            )
            Text(
                text =
                    stringResource(
                        if (uploading) Res.string.wizard_submit_uploading_hint else Res.string.wizard_submit_hint,
                    ),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}
